import {
  Accordion,
  Tabs,
  Button,
  Container,
  Center,
  Space,
  Group,
  Stack,
  Flex,
  FocusTrap,
  Paper,
  Text,
  Title,
} from '@mantine/core';
import {
  modals,
} from '@mantine/modals';
import ShoppingBasketIcon from '@mui/icons-material/ShoppingBasket';
import {
  createFileRoute,
} from '@tanstack/react-router';
import React, {
  useMemo,
  useState,
} from 'react';

import {
  UsageSlider,
  type SubscriptionStaticData,
} from '#components/ui/input/UsageSlider';
import {
  CancelSubscription,
} from '#src/features/payment/CancelSubscription';
import {
  Subscribe,
} from '#features/payment/Subscribe';
import {
  formatAud,
  resolveObject,
} from '@specia-accountant/shared';
import {
  PlanCards,
  type PricingPlan,
} from '#components/ui/card/PlanCards'

interface PlanDetails {
  subscription_id: string;
  ssid: string;
  microsoft_subscription_id: string;
  subscription_status: string;
  plan_id: string;
  subscription_start_at: Date;
  subscription_suspened_at: Date;
  subscription_reinstated_at: Date;
  subscription_canceled_at: Date;
  stripe_subscription_id: string;
  stripe_price_id: string;
  created_at: Date;
  updated_at: Date;
  next_payment_at: Date;
}
interface SubscriptionLoaderData {
  pricingPlans: {
    flatRate: PricingPlan;
    payg: PricingPlan;
  };
  clientCount: {
    noOfClients: number;
  };
  currentPlan: PlanDetails[];
}

const staticData: SubscriptionStaticData = {
  flatRatePlans: [
    {
      id: 'gary_plan2',
      plan_name: 'Small',
      description: 'Expand your capabilities and manage workpapers for up to 200 clients.',
      maxClientCount: 200,
      price: 1000,
    },
    {
      id: 'gary_plan3',
      plan_name: 'Medium',
      description: 'Scale your operations confidently with workpapers for up to 500 clients.',
      maxClientCount: 500,
      price: 2000,
    },
    {
      id: 'gary_plan4',
      plan_name: 'Large',
      description: 'Streamline complex workflows with workpapers for up to 1000 clients.',
      maxClientCount: 1000,
      price: 3000,
    },
    {
      id: 'gary_plan5',
      plan_name: 'Corporate',
      description: 'Get unlimited workpapers to support your enterprise\'s extensive needs.',
      maxClientCount: 10000000,
      price: 3500,
    },
  ],
  paygPlans: [
    {
      id: 'gary_payg_plan1',
      price: 7,
      percentage: 0,
      minClientCount: 0,
      description: 'Start at $7 per client per month with no minimum commitment. As your client base grows, you’ll unlock lower pricing. Scale up and save!',
    },
    {
      id: 'gary_payg_plan2',
      price: 6,
      percentage: 10,
      minClientCount: 100,
      description: 'Once you exceed 100 clients, your rate drops to $6 per client per month. Keep growing to 200 clients and reduce your cost even further!',
    },
    {
      id: 'gary_payg_plan3',
      price: 5,
      percentage: 25,
      minClientCount: 200,
      description: 'Surpass 200 clients and lower your cost to $5 per client per month. Scale up to 500 clients for even greater discounts!',
    },
    {
      id: 'gary_payg_plan4',
      price: 4,
      percentage: 45,
      minClientCount: 500,
      description: 'Reach 500 clients and enjoy a lower rate of $4 per client per month. Push past 1,000 clients to unlock even more savings!',
    },
    {
      id: 'gary_payg_plan5',
      price: 3,
      percentage: 65,
      minClientCount: 1000,
      description: 'Exceed 1,000 clients and benefit from a reduced rate of $3 per client per month. Keep scaling to 2,000 clients and pay even less!',
    },
    {
      id: 'gary_payg_plan6',
      price: 2,
      percentage: 85,
      minClientCount: 2000,
      description: 'Once you surpass 2,000 clients, you get the lowest rate of just $2 per client per month. The bigger your business, the more you save!',
    },
  ],
};

export const Route = createFileRoute('/_app/_authenticated/settings/subscription')({
  component: RouteComponent,
  loader: async (opts) => {
    const pricingPlans = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'stripe',
        'plan',
      ],
    });
    const currentPlan = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'stripe',
        'plan',
        'current',
      ],
    });
    const clientCount = opts.context.queryClient.ensureQueryData({
      queryKey: [
        'client',
        'count',
      ],
    });
    return resolveObject({
      pricingPlans,
      currentPlan,
      clientCount,
    });
  },
});

function RouteComponent(): React.ReactNode {
  const {
    paygPlans,
    flatRatePlans,
  } = staticData;
  const {
    pricingPlans,
    clientCount,
    currentPlan,
  } = Route.useLoaderData() as SubscriptionLoaderData;
  const [
    modalId,
    setModalId,
  ] = useState<string>('');
  const [
    selectedFixedPlan,
    setSelectedFixedPlan,
  ] = useState<PricingPlan | null>(currentPlan.length ? currentPlan[0].subscription_id : null);
  const [
    selectedPaygPlan,
    setSelectedPaygPlan,
  ] = useState<PricingPlan | null>(currentPlan.length ? currentPlan[0].subscription_id : null);
  const [
    paygCurrentPercentage,
    setPaygCurrentPercentage,
  ] = useState<number>(0);
  const [
    count,
    setCount,
  ] = useState<number>(1);
  const [
    activeTab,
    setActiveTab,
  ] = useState<string | null>('payg');
  function selectPlansByClientCount(clientCount: number) {
    if (clientCount) {
      for (const [i, paygPlan] of paygPlans.entries()) {
        if (i === (paygPlans.length - 1)) { // If this is the last plan size, select this plan
          setSelectedPaygPlan(paygPlans[i]);
        } else if (paygPlans[i + 1].minClientCount > clientCount) { // If the next plan's minimum is not met, select this plan
          setSelectedPaygPlan(paygPlans[i]);
          break;
        }
      }
      let calculatedFixedPlan;
      for (const plan of flatRatePlans) {
        if (clientCount < plan.maxClientCount) {
          calculatedFixedPlan = plan;
          break;
        }
      }
      // console.debug('Flat rate plan size', calculatedFixedPlan);
      setSelectedFixedPlan(calculatedFixedPlan);

      setCount(clientCount);
    } else {
      console.error('Failed to get client count');
    }
  }

  useMemo(() => {
    selectPlansByClientCount(clientCount.noOfClients);
  }, [clientCount.noOfClients]);

  return (
    <Stack
      h="100%"
      align="stretch"
      justify="flex-start"
    >
      <Title
        order={ 4 }
        fw={ 600 }
        size="xl"
        mb="xl"
        c="transparent.7"
      >
        Subscription
      </Title>
      <Text
        size="md"
        c="transparent.6"
        fw={ 400 }
        mb="xl"
      >
        Manage your subscription
      </Text>
      <UsageSlider
        staticData={ staticData }
        defaultValue={ 90 }
        onChangeEnd={ (clientCount) => {
          // console.debug('UsageSlider value changed', clientCount);
          selectPlansByClientCount(clientCount);
        } }
      />
      <Tabs
        variant="pills"
        color="primary.9"
        value={activeTab}
        onChange={setActiveTab}
        autoContrast
      >
        <Tabs.List
          grow
        >
          <Tabs.Tab
            value="payg"
            size="xl"
          >
            Pay As You Go
          </Tabs.Tab>
          <Tabs.Tab
            value="flatrate"
            size="xl"
          >
            Flat Rate
          </Tabs.Tab>
        </Tabs.List>
        <Tabs.Panel value="payg">
          <Stack
            mt="xl"
          >
            <Text
              size="md"
              ta="left"
              c="transparent.8"
              my="0"
              mx="md"
            >
              { `Estimated cost: ${formatAud(selectedPaygPlan?.price ?? 7)} × ${count} ${count === 1 ? 'Client' : 'Clients'} = ${formatAud((selectedPaygPlan?.price ?? 7) * count)}` }
            </Text>
            <PlanCards
              plans={ staticData.paygPlans }
              setSelectedPlan={ setSelectedPaygPlan }
              selectedPlan={ selectedPaygPlan }
            />
            <div
              style={ {
                display: 'flex',
                flexDirection: 'row',
                marginTop: '0vw',
                justifyContent: 'center',
                margin: '0.5vw 1vw',
                marginBottom: '1vw',
                pointerEvents: 'none',
              } }
            >

            </div>
            <Space h="lg" />
            <Space h="lg" />
          </Stack>
          { (!currentPlan || currentPlan?.length === 0) ? (
            <Center>
              <Stack
                w="100%"
                align="stretch"
                justify="flex-start"
              >

                <Text
                  ta="center"
                  c="transparent.7"
                  mb="lg"
                  size="md"
                >
                  Pricing is calculated based on the number of active clients configured during the billing period
                </Text>
                <Button
                  style={ {
                    marginBottom: '1vw',
                  } }
                  onClick={ () => {
                    setModalId(
                      modals.open({
                        title: (
                          <div
                            style={ {
                              display: 'flex',
                              flexDirection: 'row',
                              justifyContent: 'center',
                              alignItems: 'center',
                            } }
                          >
                            <ShoppingBasketIcon />
                            <Text
                              size="xl"
                              ml="0.7vw"
                            >
                              Subscription
                            </Text>
                          </div>
                        ),
                        children: (
                          <>
                            <FocusTrap.InitialFocus />
                            <Subscribe
                              modalId={ modalId }
                              plan={ {
                                name: 'Pay-as-you-go',
                                description: 'Starts at $7 per client/month',
                                priceId: pricingPlans?.payg?.stripe_price?.id ?? '',
                              } }
                            />
                          </>
                        ),
                      }),
                    );
                  } }
                >
                  Subscribe using Pay As You Go Plan
                </Button>
              </Stack>
            </Center>
          ) : (
            <Center>
              <Stack>
                <Text
                  c="transparent.7"
                  mb="xl"
                  size="md"
                >
                  { `Your next payment is due on ${currentPlan[0]?.next_payment_at}` }
                </Text>
                <Button
                  mb="md"
                  onClick={ () => {
                    const cancelModalId = modals.open({
                      size: 'md',
                      children: (
                        <>
                          <FocusTrap.InitialFocus />
                          <CancelSubscription
                            modalCloseHandler={ () => { modals.close(cancelModalId); } }
                            subscriptionId={ currentPlan[0].stripe_subscription_id }
                          />
                        </>
                      ),
                    });
                  } }
                >
                  Cancel
                </Button>
              </Stack>
            </Center>
          ) }
        </Tabs.Panel>
        <Tabs.Panel value="flatrate">
          <Center
            mx="sm"
            my="lg"
          >
            <Stack>
              <PlanCards
                plans={ staticData.flatRatePlans }
                setSelectedPlan={ setSelectedFixedPlan }
                selectedPlan={ selectedFixedPlan }
              />
              <Space h="lg" />
              <Space h="lg" />
              <Space h="lg" />
              <Button
                style={ {
                  marginBottom: '1vw',
                } }
                onClick={ () => {
                  setModalId(
                    modals.open({
                      closeOnClickOutside: true,
                      withCloseButton: true,
                      withinPortal: false,
                      title: (
                        <Flex
                          justify="center"
                          align="center"
                          direction="row"
                        >
                          <ShoppingBasketIcon />
                          <Text
                            size="xl"
                            ml="sm"
                          >
                            Subscription
                          </Text>
                        </Flex>
                      ),
                      children: (
                        <>
                          <FocusTrap.InitialFocus />
                          <Subscribe
                            modalId={ modalId }
                            plan={ {
                              name: 'Flat Rate',
                              description: 'Volume discounts based on subscription client count starting at $1000 per month for 200 clients or less, unlimited usage with no extra cost after the first 2000 clients',
                              priceId: pricingPlans?.flatRate?.stripe_price?.id ?? '',
                            } }
                          />
                        </>
                      ),
                    }),
                  );
                } }
              >
                Subscribe using Flat Rate Plan
              </Button>
            </Stack>
          </Center>
        </Tabs.Panel>
      </Tabs>
    </Stack>
  );
}
